import Store from '../flux/Store';
import config from '../config';
import mapActions from '../actions/map';
import zoomerActions from '../actions/zoomer';
import { parse } from '../utils/parsers';
import { parallel } from '../utils/async';
import { fetchAvailability, fetchGeometry } from '../api';

const perfStart = (name) => process.env.NODE_ENV !== 'production' && console && console.time && console.time(name);
const perfEnd = (name) => process.env.NODE_ENV !== 'production' && console && console.timeEnd && console.timeEnd(name);

/** @class MapStore */
class MapStore extends Store {

  init () {
    mapActions.on(mapActions.EVENTS.SEAT_TOGGLE, this.toggleSeat.bind(this));
    mapActions.on(mapActions.EVENTS.SEAT_REMOVE, this.removeSeat.bind(this));
    mapActions.on(mapActions.EVENTS.SEAT_REMOVE_ALL, this.removeAllSeats.bind(this));

    mapActions.on(mapActions.EVENTS.LOAD, this.loadMap.bind(this));
    mapActions.on(mapActions.EVENTS.LOAD_SUCCESS, this.loadedMap.bind(this));
    mapActions.on(mapActions.EVENTS.LOAD_ERROR, this.errorLoadingMap.bind(this));

    mapActions.on(mapActions.EVENTS.RELOAD_AVAILABILITY, this.reloadAvailability.bind(this));
    mapActions.on(mapActions.EVENTS.REFRESH_AVAILABILITY, this.refreshAvailability.bind(this));

    mapActions.on(mapActions.EVENTS.PRICE_RANGE_TOGGLE, this.togglePriceRange.bind(this));
    mapActions.on(mapActions.EVENTS.SECTION_TOGGLE, this.toggleActiveSection.bind(this));
    mapActions.on(mapActions.EVENTS.SECTION_RESET, this.resetActiveSection.bind(this));

    zoomerActions.on(zoomerActions.EVENTS.RESET, this.onZoomReset.bind(this));
  }

  onZoomReset () {
    this.data.activeSection = false;
    this.done();
  }

  // LOADING AVAILABILITY AND GEOMETRY
  // -------------------------------------------------------------------------------------------------------
  loadMap () {
    this.reset();

    if (config.availability.url && config.geometry.url) {
      // request in parallel the geometry and availability
      perfStart('sim-loading-data');
      parallel({
        availability: fetchAvailability,
        geometry: fetchGeometry
      }, function (err, response) {
        // the first request that fails, both fail
        perfEnd('sim-loading-data');
        if (err) {
          console.error('Error fetching the availability or geometry data');
          return mapActions.emit(mapActions.EVENTS.LOAD_ERROR);
        }
        perfStart('sim-parsers');
        // both requests where successful, pass them to the parsing phase
        mapActions.emit(mapActions.EVENTS.LOAD_SUCCESS, response.geometry, response.availability);
      });
    }

    this.done();
  }

  /**
   * This function is called at initialization. It will parse the geometry assigning the current availability.
   * @param geometry
   * @param availability
   */
  loadedMap (geometry, availability) {

    this.data.rawAvailability = availability;
    this.data.rawGeometry = geometry;

    perfStart('availability_parser');
    const parsedAvailability = parse('availability', availability);

    if (parsedAvailability instanceof Error) {
      console.error('Error parsing geometry:\n', parsedAvailability);
      this.data.rawAvailability = false;
      return mapActions.emit(mapActions.EVENTS.LOAD_ERROR);
    }
    perfEnd('availability_parser');

    // calculate geometry knowing the availability info
    perfStart('geometry_parser');
    this.data.geometry = parse('geometry', geometry, parsedAvailability);
    perfEnd('geometry_parser');

    if (this.data.geometry instanceof Error) {
      console.error('Error parsing geometry:\n', this.data.geometry);
      this.data.rawAvailability = false;
      this.data.rawGeometry = false;
      this.data.geometry = false;
      return mapActions.emit(mapActions.EVENTS.LOAD_ERROR);
    }
    perfEnd('sim-parsers');

    this.done();
    this.setReloadTimeout();
  }

  /**
   * Reload the availability after a determined amount of time
   * configured through config.availability.refresh value
   * set it to -1 to switch off refreshing
   */
  setReloadTimeout () {
    if (config.availability.refresh >= 0) {
      setTimeout(function () {
        mapActions.emit(mapActions.EVENTS.RELOAD_AVAILABILITY);
      }, config.availability.refresh);
    }
  }

  /**
   * This function performs the fetch of the updated availability and then, if successfully, refreshes the data
   */
  reloadAvailability () {
    // reload the availability
    if (config.availability.url) {

      fetchAvailability((err, availability) => {
        if (err) { // todo what? maybe just ignore? maybe reset counter?
          return this.setReloadTimeout();
        }
        // parse the new availability and refresh the map
        this.refreshAvailability(availability);
      });
    }
  }

  /**
   * This function is called during availability refresh.
   * It updates the availability on the current geometry
   * @param {*} newAvailability
   */
  refreshAvailability (newAvailability) {
    var parsedAvailability = parse('availability', newAvailability);
    // If we get any error, keep the old availability and surface an error
    if (parsedAvailability instanceof Error) {
      return console.error('Error parsing availability:\n', parsedAvailability);
    }
    // only update the availability if the new one is properly parsed
    this.data.rawAvailability = newAvailability;

    // FIXME, improve performance on recalculate geometry knowing the availability info
    this.data.geometry = parse('geometry', this.data.rawGeometry, parsedAvailability);

    if (this.data.geometry instanceof Error) {
      console.error('Error parsing geometry:\n', this.data.geometry);
      this.data.rawAvailability = false;
      this.data.rawGeometry = false;
      this.data.geometry = false;
      return mapActions.emit(mapActions.EVENTS.LOAD_ERROR);
    }

    this.done();
    this.setReloadTimeout();
  }

  // updateSingleSeat (seatIds, newAvail) {
  //   seatIds = Array.isArray(seatIds) ? seatIds : [seatIds];

  //   seatIds.forEach(function (seatId) {
  //     // console.log(this.data);
  //     var newAvailability = this.data.availability;
  //     newAvailability[seatId].available = newAvail;

  //     var seatSplit = seatId.split('-');
  //     var sectionCode = seatSplit[0];

  //     var sections = this.data.geometry.filter(function (section) {
  //       return section.name === sectionCode;
  //     });

  //     sections.forEach(function (section) {
  //       section.rows.forEach(function (row) {
  //         row.seats.forEach(function (seat) {
  //           if (seat.id === seatId) {
  //             seat.available = newAvail;
  //             row.availableSeatCount--;
  //             section.availableSeatCount--;
  //           }
  //         });
  //       });
  //     });
  //   }, this);

  //   this.done();
  // }

  errorLoadingMap () {
    this.data.fatalError = true;
    this.data.errors.push({
      type:    'error',
      message: 'Error loading the map, please try again'
    });
    this.done();
  }

  // SEATS / SECTIONS TOGGLE
  // -------------------------------------------------------------------------------------------------------

  toggleSeat (seat) {
    var seatIndex = this.data.activeSeats.indexOf(seat.id);
    if (seatIndex === -1) {
      this.data.activeSeats.push(seat.id);
    }
    else {
      this.data.activeSeats.splice(seatIndex, 1);
    }

    this.data.activeSeats = Store.deepClone(this.data.activeSeats);

    this.done();
  }

  toggleActiveSection (newSectionId) {
    if (this.data.activeSection !== newSectionId) {
      this.data.activeSection = newSectionId;
      this.done();
    }
  }

  resetActiveSection () {
    this.data.activeSection = false;
    this.done();
  }

  togglePriceRange (priceRangeCode) {
    var priceRangeIndex = this.data.activePriceCategories.indexOf(priceRangeCode);

    if (priceRangeIndex === -1) {
      this.data.activePriceCategories.push(priceRangeCode);
    }
    else {
      this.data.activePriceCategories.splice(priceRangeIndex, 1);
    }
    this.done();
  }

  removeSeat (seatId) {
    var seatIndex = this.data.activeSeats.indexOf(seatId);
    if (seatIndex !== -1) {
      this.data.activeSeats.splice(seatIndex, 1);
      this.data.activeSeats = Store.deepClone(this.data.activeSeats);
    }
    this.done();
  }

  removeAllSeats () {
    this.data.activeSeats = [];
    this.done();
  }

  /**
   * Goes through all the geometry until it finds an orphan seat
   * This can also be called from the oustide to performa a check whenever needed
   */
  hasSingleSeat () {
    // keep track if we found an orphan seat
    var hasOrphanSeat = false;

    /**
     * Blocks look like this:
     * "aaassaaaa" where
     * "a" = available
     * "s" = selected
     * @param {String!} block
     */
    var checkBlockOrphans = function (block) {
      var splitBlock = block.split('s');
      for (var i = 0; i < splitBlock.length; ++i) {
        if (splitBlock[i].length === 1) {
          // we found the orphan
          hasOrphanSeat = true;
          return;
        }
      }
    };

    // loop through all the geometry
    this.data.geometry.forEach(section => {
      // if we already established there is an error, ignore the rest
      if (hasOrphanSeat) return;

      section.rows.forEach(row => {
        // if we already established there is an error, ignore the rest
        if (hasOrphanSeat) return;

        // find the available blocks per row
        var checkBlock = false;
        var currentBlock = '';

        row.seats.forEach(seat => {
          // if we already established there is an error, ignore the rest
          if (hasOrphanSeat) return;

          // check if seat is selected, increment block with the selected seat and flag that this block needs checking
          if (this.data.activeSeats.indexOf(seat.id) !== -1) {
            checkBlock = true;
            return currentBlock += 's';  // selected, next!
          }

          // check if its available, increment block with an available seat
          if (seat.available) {
            return currentBlock += 'a'; // available, next!
          }

          // we hit an unavailable seat, test the current block if testable
          if (checkBlock) {
            // this will set hasOrphanSeat to true if orphan seat is left
            checkBlockOrphans(currentBlock);
          }

          // done checking this block, reset and move on
          checkBlock = false;
          currentBlock = '';

        });

        // finished the line, check the remaining block
        if (checkBlock) checkBlockOrphans(currentBlock);
      });

    });

    return hasOrphanSeat;
  }
}

/** @type {MapStore} */
export default new MapStore({

  url: {
    availability: null,
    geometry:     null,
    img:          null
  },

  // here is the main store data
  rawAvailability:false,
  rawGeometry:    false,
  geometry:       false,
  errors:         [],
  fatalError:     false,

  // here below we'll keep track of user actions and selections
  activeSeats:           [],
  activeSection:         false,
  activePriceCategories: [],

  isChanging: false,

  tooltip: {
    data: null,
    el:   null
  }
});



// WEBPACK FOOTER //
// ./frontend/react/sim/core/stores/map.js